// Campus-admin chrome — dark sidebar mounted once at the (campus)
// layout level so navigation between pages doesn't unmount the shell
// or flash the nav. Mirrors the AdminShell pattern at a lighter scope:
// 4 nav items, school logo + name in the topbar, primary_color_hex
// drives the accent if the school has set one (else fall back to AMBER).
//
// Self-resolves identity from useAuth + getMyUniversityProfile; the
// inner pages never need to read scope themselves.

import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { usePathname, useRouter } from "expo-router";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { signOutToHome } from "../../lib/auth";
import { useAuth } from "../../lib/authContext";
import { getMyUniversityProfile, type UniversityProfile } from "../../lib/campus";
import { useShellResponsive } from "../../lib/useShellResponsive";
import { HamburgerButton, ShellMobileDrawer } from "../ShellMobileDrawer";

const BLACK = COLORS.BLACK.hex;
const FOREST = COLORS.FOREST.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const SIDEBAR_W = 240;
const TOPBAR_H = 52;

type NavKey = "dashboard" | "students" | "cases" | "profile";

type NavRow = { key: NavKey; label: string; href: Href };
const NAV_ROWS: NavRow[] = [
  { key: "dashboard", label: "Dashboard", href: "/campus" as Href },
  { key: "students", label: "Students", href: "/campus/students" as Href },
  { key: "cases", label: "Cases", href: "/campus/cases" as Href },
  { key: "profile", label: "School profile", href: "/campus/profile" as Href },
];

function navKeyFromPath(path: string): NavKey {
  const p = (path.split("?")[0] ?? "").replace(/\/+$/, "") || "/";
  if (p === "/campus/students" || p.startsWith("/campus/students/")) return "students";
  if (p === "/campus/cases" || p.startsWith("/campus/cases/") || p.startsWith("/campus/competition")) return "cases";
  if (p === "/campus/profile") return "profile";
  return "dashboard";
}

export function CampusShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, role } = useAuth();
  const { isMobile, showSidebar, drawerOpen, openDrawer, closeDrawer } = useShellResponsive();

  const [profile, setProfile] = useState<UniversityProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (role !== "campus_admin") return;
      const p = await getMyUniversityProfile();
      if (!cancelled) setProfile(p);
    })();
    return () => { cancelled = true; };
  }, [role]);

  const accentColor = profile?.primary_color_hex && profile.primary_color_hex.length > 0
    ? profile.primary_color_hex
    : AMBER;

  const activeKey = navKeyFromPath(pathname ?? "/campus");

  const navTo = useCallback(
    (href: Href) => {
      closeDrawer();
      router.push(href);
    },
    [closeDrawer, router],
  );

  const sidebarBody = (
    <View style={{ flex: 1, paddingVertical: 18, paddingHorizontal: 14 }}>
      {/* Brand */}
      <View style={{ marginBottom: 22, flexDirection: "row", alignItems: "center", gap: 10 }}>
        {profile?.logo_url ? (
          <Image
            source={{ uri: profile.logo_url }}
            style={{ width: 28, height: 28, borderRadius: 4, backgroundColor: "rgba(255,255,255,0.04)" }}
            resizeMode="contain"
          />
        ) : (
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 4,
              backgroundColor: "rgba(181,135,58,0.16)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text style={{ fontFamily: "CormorantGaramond_600SemiBold", fontSize: 14, color: accentColor }}>
              {(profile?.name ?? "C").charAt(0)}
            </Text>
          </View>
        )}
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 14, color: LINEN }} numberOfLines={1}>
            {profile?.name ?? "Campus portal"}
          </Text>
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
            Campus admin
          </Text>
        </View>
      </View>

      {/* Nav */}
      <View style={{ gap: 2 }}>
        {NAV_ROWS.map((row) => {
          const active = row.key === activeKey;
          return (
            <Pressable
              key={row.key}
              onPress={() => navTo(row.href)}
              style={[
                {
                  paddingHorizontal: 12,
                  paddingVertical: 9,
                  borderRadius: 4,
                  backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
                  borderLeftWidth: 2,
                  borderLeftColor: active ? accentColor : "transparent",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12.5, color: active ? accentColor : WS }}>
                {row.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={{ flex: 1 }} />

      {/* Footer — sign out + user identity */}
      <View style={{ borderTopWidth: 1, borderTopColor: BORDER, paddingTop: 12, gap: 6 }}>
        <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 11, color: WM }} numberOfLines={1}>
          {user?.email ?? "—"}
        </Text>
        <Pressable
          onPress={() => void signOutToHome(router)}
          style={[
            { paddingVertical: 6 } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, letterSpacing: 0.4, color: WL }}>
            Sign out
          </Text>
        </Pressable>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BLACK }} edges={["top", "left", "right"]}>
      <View style={{ flex: 1, flexDirection: "row", backgroundColor: BLACK }}>
        {showSidebar ? (
          <View style={{ width: SIDEBAR_W, backgroundColor: FOREST, borderRightWidth: 1, borderRightColor: BORDER }}>
            {sidebarBody}
          </View>
        ) : null}

        <View style={{ flex: 1, minWidth: 0 }}>
          {/* Topbar */}
          <View
            style={{
              height: TOPBAR_H,
              paddingHorizontal: isMobile ? 12 : 22,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              borderBottomWidth: 1,
              borderBottomColor: BORDER,
              backgroundColor: BLACK,
            }}
          >
            {isMobile ? <HamburgerButton onPress={openDrawer} color={LINEN} /> : null}
            <Text
              style={{ flexShrink: 1, fontFamily: "DMSans_500Medium", fontSize: 13, color: LINEN }}
              numberOfLines={1}
            >
              {NAV_ROWS.find((r) => r.key === activeKey)?.label ?? "Campus portal"}
            </Text>
            <View style={{ flex: 1 }} />
            {profile?.website_url ? (
              <Pressable
                onPress={() => {
                  if (Platform.OS === "web" && typeof window !== "undefined" && profile.website_url) {
                    window.open(profile.website_url, "_blank");
                  }
                }}
                style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
              >
                <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: WM }}>
                  {profile.website_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </Text>
              </Pressable>
            ) : null}
          </View>

          {/* Body */}
          <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
            {children}
          </ScrollView>
        </View>
      </View>

      <ShellMobileDrawer open={drawerOpen} onRequestClose={closeDrawer} backgroundColor={FOREST}>
        {sidebarBody}
      </ShellMobileDrawer>
    </SafeAreaView>
  );
}

// Re-exported palette for child pages that want to match the shell.
export const CAMPUS_PALETTE = {
  BLACK,
  FOREST,
  FOREST_MID,
  AMBER,
  LINEN,
  W,
  WS,
  WM,
  WL,
  BORDER,
};
